import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "../i18n";
import { FileText, ArrowLeft, CheckCircle, AlertCircle, Scale, Ban, Gavel, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import Seo from "../components/Seo";

const TermsOfService = () => {
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(i18n.language?.split("-")[0] || "fr");

  const changeLang = (lang) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang);
  };

  const t = (obj) => obj[currentLang] || obj.fr;

  return (
    <div
      className="dark min-h-screen"
      style={{ backgroundColor: "var(--lm-canvas)", fontFamily: "Inter, system-ui, sans-serif" }}
    >
      <Seo
        path="/terms"
        title="Conditions d'Utilisation — Mawered"
        description="Conditions générales d'utilisation de Mawered (Teknovat) : accès au service, obligations, responsabilités et cadre juridique."
      />
      {/* Header */}
      <div style={{ backgroundColor: "var(--lm-s1)", borderBottom: "1px solid var(--lm-border)" }}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate("/")} className="hover:bg-muted">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t({ fr: "Retour", en: "Back", ar: "عودة" })}
            </Button>

            {/* Language Switcher */}
            <div className="flex gap-2">
              <Button variant={currentLang === "fr" ? "default" : "outline"} size="sm" onClick={() => changeLang("fr")}>
                FR
              </Button>
              <Button variant={currentLang === "en" ? "default" : "outline"} size="sm" onClick={() => changeLang("en")}>
                EN
              </Button>
              <Button variant={currentLang === "ar" ? "default" : "outline"} size="sm" onClick={() => changeLang("ar")}>
                AR
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-muted text-muted-foreground hover:bg-muted">
            <FileText className="w-3 h-3 mr-1" />
            {t({
              fr: "Conditions Générales",
              en: "Terms of Service",
              ar: "شروط الخدمة",
            })}
          </Badge>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            {t({
              fr: "Conditions Générales d'Utilisation",
              en: "Terms of Service",
              ar: "شروط الخدمة",
            })}
          </h1>
          <p className="text-muted-foreground">
            {t({
              fr: "Dernière mise à jour : Décembre 2024",
              en: "Last updated: December 2024",
              ar: "آخر تحديث: ديسمبر 2024",
            })}
          </p>
        </div>

        <div className="space-y-6">
          {/* 1. Introduction */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "Introduction",
                    en: "Introduction",
                    ar: "المقدمة",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                {t({
                  fr: "Bienvenue sur Mawered, la plateforme de gestion des ressources humaines. En accédant à notre service, vous acceptez d'être lié par les présentes conditions générales d'utilisation.",
                  en: "Welcome to Mawered, the human resources management platform. By accessing our service, you agree to be bound by these terms of service.",
                  ar: "مرحبًا بك في Mawered، منصة إدارة الموارد البشرية. من خلال الوصول إلى خدمتنا، فإنك توافق على الالتزام بشروط الخدمة هذه.",
                })}
              </p>
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <p className="font-semibold text-foreground">
                  {t({
                    fr: "Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre plateforme.",
                    en: "If you do not accept these terms, please do not use our platform.",
                    ar: "إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام منصتنا.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 2. Service Description */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "1. Description du Service",
                    en: "1. Service Description",
                    ar: "1. وصف الخدمة",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                {t({
                  fr: "Mawered fournit une solution complète de gestion RH incluant :",
                  en: "Mawered provides a comprehensive HR management solution including:",
                  ar: "تقدم Mawered حلاً شاملاً لإدارة الموارد البشرية يشمل:",
                })}
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  {t({
                    fr: "Gestion des employés et des données RH",
                    en: "Employee management and HR data",
                    ar: "إدارة الموظفين وبيانات الموارد البشرية",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Gestion de la paie et des congés",
                    en: "Payroll and leave management",
                    ar: "إدارة الرواتب والإجازات",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Pointage et suivi de présence via ZKTeco",
                    en: "Attendance tracking via ZKTeco",
                    ar: "تتبع الحضور عبر ZKTeco",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Recrutement assisté par IA",
                    en: "AI-powered recruitment",
                    ar: "التوظيف المدعوم بالذكاء الاصطناعي",
                  })}
                </li>
                <li>
                  {t({
                    fr: "Génération de documents et rapports",
                    en: "Document and report generation",
                    ar: "إنشاء المستندات والتقارير",
                  })}
                </li>
              </ul>
              <div className="bg-muted/50 border rounded-lg p-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  {t({
                    fr: "Les fonctionnalités disponibles dépendent de votre plan d'abonnement (Starter, Professional, ou Enterprise).",
                    en: "Available features depend on your subscription plan (Starter, Professional, or Enterprise).",
                    ar: "تعتمد الميزات المتاحة على خطة اشتراكك (Starter أو Professional أو Enterprise).",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 3. User Obligations */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "2. Obligations de l'Utilisateur",
                    en: "2. User Obligations",
                    ar: "2. التزامات المستخدم",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Compte Utilisateur",
                    en: "User Account",
                    ar: "حساب المستخدم",
                  })}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                  <li>
                    {t({
                      fr: "Vous êtes responsable de maintenir la confidentialité de vos identifiants",
                      en: "You are responsible for maintaining the confidentiality of your credentials",
                      ar: "أنت مسؤول عن الحفاظ على سرية بيانات اعتمادك",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Vous devez fournir des informations exactes et complètes",
                      en: "You must provide accurate and complete information",
                      ar: "يجب عليك تقديم معلومات دقيقة وكاملة",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Vous êtes responsable de toutes les activités sous votre compte",
                      en: "You are responsible for all activities under your account",
                      ar: "أنت مسؤول عن جميع الأنشطة تحت حسابك",
                    })}
                  </li>
                </ul>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Utilisation Acceptable",
                    en: "Acceptable Use",
                    ar: "الاستخدام المقبول",
                  })}
                </h4>
                <p className="text-muted-foreground mb-2">
                  {t({
                    fr: "Vous vous engagez à ne pas :",
                    en: "You agree not to:",
                    ar: "أنت توافق على عدم:",
                  })}
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                  <li>
                    {t({
                      fr: "Utiliser le service pour des activités illégales",
                      en: "Use the service for illegal activities",
                      ar: "استخدام الخدمة لأنشطة غير قانونية",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Tenter d'accéder de manière non autorisée à nos systèmes",
                      en: "Attempt unauthorized access to our systems",
                      ar: "محاولة الوصول غير المصرح به إلى أنظمتنا",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Transmettre des virus ou codes malveillants",
                      en: "Transmit viruses or malicious code",
                      ar: "نقل الفيروسات أو الأكواد الضارة",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Copier, modifier ou distribuer notre contenu sans autorisation",
                      en: "Copy, modify or distribute our content without authorization",
                      ar: "نسخ أو تعديل أو توزيع محتوانا بدون إذن",
                    })}
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 4. Intellectual Property */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Scale className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "3. Propriété Intellectuelle",
                    en: "3. Intellectual Property",
                    ar: "3. الملكية الفكرية",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                {t({
                  fr: "Tous les droits de propriété intellectuelle liés à la plateforme Mawered, y compris mais sans s'y limiter : le code source, les designs, les logos, les marques, et le contenu, sont la propriété exclusive de Mawered SARL.",
                  en: "All intellectual property rights related to the Mawered platform, including but not limited to: source code, designs, logos, trademarks, and content, are the exclusive property of Mawered SARL.",
                  ar: "جميع حقوق الملكية الفكرية المتعلقة بمنصة Mawered، بما في ذلك على سبيل المثال لا الحصر: الكود المصدري والتصاميم والشعارات والعلامات التجارية والمحتوى، هي ملكية حصرية لـ Mawered SARL.",
                })}
              </p>
              <div className="bg-amber-950/20 border border-amber-900/40 rounded-lg p-4">
                <p className="text-sm font-semibold text-amber-400">
                  {t({
                    fr: "Toute utilisation non autorisée de notre propriété intellectuelle peut entraîner des poursuites judiciaires.",
                    en: "Any unauthorized use of our intellectual property may result in legal action.",
                    ar: "أي استخدام غير مصرح به لملكيتنا الفكرية قد يؤدي إلى اتخاذ إجراءات قانونية.",
                  })}
                </p>
              </div>
              <p className="text-muted-foreground">
                {t({
                  fr: "Vos données restent votre propriété. Vous nous accordez une licence limitée pour les traiter dans le cadre de la fourniture du service.",
                  en: "Your data remains your property. You grant us a limited license to process it as part of providing the service.",
                  ar: "تظل بياناتك ملكيتك. أنت تمنحنا ترخيصًا محدودًا لمعالجتها كجزء من تقديم الخدمة.",
                })}
              </p>
            </CardContent>
          </Card>

          {/* 5. Subscriptions and Payments */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "4. Abonnements et Paiements",
                    en: "4. Subscriptions and Payments",
                    ar: "4. الاشتراكات والمدفوعات",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Plans d'Abonnement",
                    en: "Subscription Plans",
                    ar: "خطط الاشتراك",
                  })}
                </h4>
                <p className="text-muted-foreground">
                  {t({
                    fr: "Les prix et fonctionnalités de chaque plan (Starter, Professional, Enterprise) sont détaillés sur notre page tarifaire. Les prix sont en Dinars Tunisiens (DT) et hors taxes.",
                    en: "The prices and features of each plan (Starter, Professional, Enterprise) are detailed on our pricing page. Prices are in Tunisian Dinars (DT) and exclude taxes.",
                    ar: "يتم تفصيل أسعار وميزات كل خطة (Starter و Professional و Enterprise) في صفحة التسعير الخاصة بنا. الأسعار بالدينار التونسي (DT) وغير شاملة للضرائب.",
                  })}
                </p>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Facturation",
                    en: "Billing",
                    ar: "الفوترة",
                  })}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                  <li>
                    {t({
                      fr: "Les abonnements sont facturés mensuellement ou annuellement selon votre choix",
                      en: "Subscriptions are billed monthly or annually based on your choice",
                      ar: "يتم إصدار فواتير الاشتراكات شهريًا أو سنويًا بناءً على اختيارك",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Les paiements sont effectués par virement bancaire ou carte de crédit",
                      en: "Payments are made by bank transfer or credit card",
                      ar: "تتم المدفوعات عن طريق التحويل البنكي أو بطاقة الائتمان",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "En cas de non-paiement, l'accès au service peut être suspendu",
                      en: "In case of non-payment, access to the service may be suspended",
                      ar: "في حالة عدم الدفع، قد يتم تعليق الوصول إلى الخدمة",
                    })}
                  </li>
                </ul>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Remboursements",
                    en: "Refunds",
                    ar: "المبالغ المستردة",
                  })}
                </h4>
                <p className="text-muted-foreground">
                  {t({
                    fr: "Nous offrons une garantie de remboursement de 14 jours pour les nouveaux clients. Après cette période, les paiements ne sont pas remboursables sauf cas exceptionnels évalués au cas par cas.",
                    en: "We offer a 14-day money-back guarantee for new customers. After this period, payments are non-refundable except in exceptional cases evaluated on a case-by-case basis.",
                    ar: "نقدم ضمان استرداد الأموال لمدة 14 يومًا للعملاء الجدد. بعد هذه الفترة، لا يتم استرداد المدفوعات إلا في حالات استثنائية يتم تقييمها على أساس كل حالة على حدة.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 6. Liability and Warranties */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Ban className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "5. Responsabilité et Garanties",
                    en: "5. Liability and Warranties",
                    ar: "5. المسؤولية والضمانات",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Disponibilité du Service",
                    en: "Service Availability",
                    ar: "توفر الخدمة",
                  })}
                </h4>
                <p className="text-muted-foreground">
                  {t({
                    fr: "Nous nous efforçons de maintenir une disponibilité de 99.9% du service. Cependant, nous ne pouvons garantir un accès ininterrompu en raison de maintenances, mises à jour ou circonstances exceptionnelles.",
                    en: "We strive to maintain 99.9% service availability. However, we cannot guarantee uninterrupted access due to maintenance, updates or exceptional circumstances.",
                    ar: "نسعى جاهدين للحفاظ على توفر الخدمة بنسبة 99.9٪. ومع ذلك، لا يمكننا ضمان الوصول دون انقطاع بسبب الصيانة أو التحديثات أو الظروف الاستثنائية.",
                  })}
                </p>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Limitation de Responsabilité",
                    en: "Limitation of Liability",
                    ar: "حدود المسؤولية",
                  })}
                </h4>
                <p className="text-muted-foreground">
                  {t({
                    fr: "Mawered ne pourra être tenu responsable des dommages indirects, pertes de données, ou interruptions d'activité résultant de l'utilisation ou de l'impossibilité d'utiliser notre service, sauf en cas de négligence grave ou de faute intentionnelle.",
                    en: "Mawered cannot be held liable for indirect damages, data loss, or business interruptions resulting from the use or inability to use our service, except in cases of gross negligence or intentional misconduct.",
                    ar: "لا يمكن تحميل Mawered المسؤولية عن الأضرار غير المباشرة أو فقدان البيانات أو انقطاعات الأعمال الناتجة عن استخدام أو عدم القدرة على استخدام خدمتنا، إلا في حالات الإهمال الجسيم أو السلوك المتعمد.",
                  })}
                </p>
              </div>

              <div className="bg-red-950/20 border border-red-900/40 rounded-lg p-4">
                <p className="text-sm font-semibold text-foreground">
                  {t({
                    fr: "En tout état de cause, notre responsabilité totale ne dépassera pas le montant des frais payés par vous au cours des 12 derniers mois.",
                    en: "In any case, our total liability will not exceed the amount of fees paid by you over the last 12 months.",
                    ar: "في أي حال، لن تتجاوز مسؤوليتنا الإجمالية مبلغ الرسوم المدفوعة من قبلك خلال الـ 12 شهرًا الماضية.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 7. Termination */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Ban className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "6. Résiliation",
                    en: "6. Termination",
                    ar: "6. الإنهاء",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Résiliation par l'Utilisateur",
                    en: "Termination by User",
                    ar: "الإنهاء من قبل المستخدم",
                  })}
                </h4>
                <p className="text-muted-foreground">
                  {t({
                    fr: "Vous pouvez résilier votre abonnement à tout moment depuis votre tableau de bord. La résiliation prendra effet à la fin de votre période de facturation en cours.",
                    en: "You can cancel your subscription at any time from your dashboard. The cancellation will take effect at the end of your current billing period.",
                    ar: "يمكنك إلغاء اشتراكك في أي وقت من لوحة التحكم الخاصة بك. سيصبح الإلغاء ساريًا في نهاية فترة الفوترة الحالية.",
                  })}
                </p>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {t({
                    fr: "Résiliation par Mawered",
                    en: "Termination by Mawered",
                    ar: "الإنهاء من قبل Mawered",
                  })}
                </h4>
                <p className="text-muted-foreground mb-2">
                  {t({
                    fr: "Nous nous réservons le droit de suspendre ou résilier votre compte en cas de :",
                    en: "We reserve the right to suspend or terminate your account in case of:",
                    ar: "نحتفظ بالحق في تعليق أو إنهاء حسابك في حالة:",
                  })}
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                  <li>
                    {t({
                      fr: "Violation des présentes conditions",
                      en: "Violation of these terms",
                      ar: "انتهاك هذه الشروط",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Non-paiement des frais",
                      en: "Non-payment of fees",
                      ar: "عدم دفع الرسوم",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Activité frauduleuse ou illégale",
                      en: "Fraudulent or illegal activity",
                      ar: "نشاط احتيالي أو غير قانوني",
                    })}
                  </li>
                  <li>
                    {t({
                      fr: "Menace pour la sécurité de nos systèmes",
                      en: "Threat to the security of our systems",
                      ar: "تهديد لأمن أنظمتنا",
                    })}
                  </li>
                </ul>
              </div>

              <div className="bg-muted/50 border rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  {t({
                    fr: "En cas de résiliation, vous aurez 30 jours pour exporter vos données avant leur suppression définitive de nos serveurs.",
                    en: "In case of termination, you will have 30 days to export your data before its permanent deletion from our servers.",
                    ar: "في حالة الإنهاء، سيكون لديك 30 يومًا لتصدير بياناتك قبل حذفها نهائيًا من خوادمنا.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 8. Governing Law */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Gavel className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "7. Droit Applicable et Juridiction",
                    en: "7. Governing Law and Jurisdiction",
                    ar: "7. القانون المطبق والاختصاص القضائي",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                {t({
                  fr: "Les présentes conditions générales sont régies par le droit tunisien. Tout litige relatif à l'interprétation ou à l'exécution des présentes sera de la compétence exclusive des tribunaux de Tunis, Tunisie.",
                  en: "These terms of service are governed by Tunisian law. Any dispute relating to the interpretation or execution of these terms will be under the exclusive jurisdiction of the courts of Tunis, Tunisia.",
                  ar: "تخضع شروط الخدمة هذه للقانون التونسي. أي نزاع يتعلق بتفسير أو تنفيذ هذه الشروط سيكون تحت الاختصاص الحصري لمحاكم تونس، تونس.",
                })}
              </p>
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  {t({
                    fr: "Nous encourageons la résolution amiable des litiges avant toute action judiciaire. N'hésitez pas à nous contacter pour discuter de toute préoccupation.",
                    en: "We encourage amicable resolution of disputes before any legal action. Please feel free to contact us to discuss any concerns.",
                    ar: "نشجع على الحل الودي للنزاعات قبل أي إجراء قانوني. لا تتردد في الاتصال بنا لمناقشة أي مخاوف.",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 9. Modifications */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "8. Modifications des Conditions",
                    en: "8. Modifications to Terms",
                    ar: "8. تعديلات على الشروط",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                {t({
                  fr: "Nous nous réservons le droit de modifier ces conditions générales à tout moment. Les modifications entreront en vigueur dès leur publication sur notre site web.",
                  en: "We reserve the right to modify these terms of service at any time. Modifications will take effect as soon as they are published on our website.",
                  ar: "نحتفظ بالحق في تعديل شروط الخدمة هذه في أي وقت. ستدخل التعديلات حيز التنفيذ بمجرد نشرها على موقعنا الإلكتروني.",
                })}
              </p>
              <p className="text-muted-foreground">
                {t({
                  fr: "En cas de modifications importantes, nous vous en informerons par email au moins 30 jours avant leur entrée en vigueur. Votre utilisation continue du service après cette notification constituera votre acceptation des nouvelles conditions.",
                  en: "In case of significant modifications, we will notify you by email at least 30 days before they take effect. Your continued use of the service after this notification will constitute your acceptance of the new terms.",
                  ar: "في حالة التعديلات المهمة، سنقوم بإخطارك عبر البريد الإلكتروني قبل 30 يومًا على الأقل من دخولها حيز التنفيذ. استمرارك في استخدام الخدمة بعد هذا الإخطار سيشكل قبولك للشروط الجديدة.",
                })}
              </p>
            </CardContent>
          </Card>

          {/* 10. Contact */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-muted-foreground" />
                <CardTitle>
                  {t({
                    fr: "9. Contact",
                    en: "9. Contact",
                    ar: "9. اتصل بنا",
                  })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                {t({
                  fr: "Pour toute question concernant ces conditions générales d'utilisation, veuillez nous contacter :",
                  en: "For any questions regarding these terms of service, please contact us:",
                  ar: "لأي أسئلة بخصوص شروط الخدمة هذه، يرجى الاتصال بنا:",
                })}
              </p>
              <div className="bg-muted/50 border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">support@mawered.com</span>
                </div>
                <p className="text-muted-foreground">
                  <strong>{t({ fr: "Adresse", en: "Address", ar: "العنوان" })}:</strong> Mawered SARL, Tunis, Tunisia
                </p>
                <p className="text-muted-foreground">
                  <strong>
                    {t({
                      fr: "Délai de réponse",
                      en: "Response time",
                      ar: "وقت الاستجابة",
                    })}
                    :
                  </strong>{" "}
                  {t({
                    fr: "Maximum 30 jours",
                    en: "Maximum 30 days",
                    ar: "بحد أقصى 30 يومًا",
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Acceptance Note */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 text-white text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">
              {t({
                fr: "Merci de Votre Confiance",
                en: "Thank You for Your Trust",
                ar: "شكرًا لثقتك",
              })}
            </h3>
            <p className="text-white/70">
              {t({
                fr: "En utilisant Mawered, vous contribuez à la transformation digitale des RH en Tunisie.",
                en: "By using Mawered, you contribute to the digital transformation of HR in Tunisia.",
                ar: "من خلال استخدام Mawered، فإنك تساهم في التحول الرقمي للموارد البشرية في تونس.",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
